import { ArrowDownIcon } from '@/components/icons'
import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'
import useUser from '@/features/auth/hooks/useUser'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as yup from 'yup'
import {
  useAddGlobal,
  useGlobalById,
  useSearchGlobal,
  useUpdateGlobal,
} from '../../hooks/globalConfiguration'
import classes from './styles.module.scss'
import { SortBy } from '@/constant/common'

import useSearchWaterSource from '../../hooks/watersource/useSearchWaterSource'
import { SearchParams } from '@/types'
import TurfQuality from './TurfQuality'

const schema = yup
  .object({
    siteArea: yup
      .number()
      .typeError('Site area size is a number')
      .positive('Site area size must be greater than 0')
      .required('Site area size is required'),
  })
  .required()

const monthOptions = [
  { value: 1, key: 'shortMonthJan' },
  { value: 2, key: 'shortMonthFeb' },
  { value: 3, key: 'shortMonthMar' },
  { value: 4, key: 'shortMonthApr' },
  { value: 5, key: 'shortMonthMay' },
  { value: 6, key: 'shortMonthJun' },
  { value: 7, key: 'shortMonthJul' },
  { value: 8, key: 'shortMonthAug' },
  { value: 9, key: 'shortMonthSep' },
  { value: 10, key: 'shortMonthOct' },
  { value: 11, key: 'shortMonthNov' },
  { value: 12, key: 'shortMonthDec' },
]

const getDaysInMonth = (month: number): number => {
  if (month === 2) return 28
  if ([4, 6, 9, 11].includes(month)) return 30
  return 31
}

const GlobalConfigurations = () => {
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const [id, setId] = useState<number>()
  const themeMode = useSelector(selectTheme)
  const { convertLabelUnit, convertToMetricV2, convertFromMetricV2 } =
    useUnits()
  const { isViewOnly } = usePermission()

  const { data: user } = useUser()
  const isMetric = !user?.measure?.id || user?.measure?.id === 1
  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
  })

  const { data } = useSearchGlobal(interfaceId)
  const { data: dataGetId } = useGlobalById(interfaceId, id)
  const { data: dataTurf, refetch } = useSearchWaterSource(
    searchParams,
    Number(interfaceId),
  )
  const [reloadTurf, setReloadTurf] = useState(0)

  const form = useForm({
    defaultValues: {
      startMonth: 1,
      startDay: 1,
    },
    resolver: yupResolver(schema as any),
  })

  const watchedMonth = Number(form.watch('startMonth')) || 1
  const maxDay = getDaysInMonth(watchedMonth)
  const dayOptions = Array.from({ length: maxDay }, (_, i) => i + 1)

  const { mutate: addGlobal, isLoading: loadingAddGlobal } = useAddGlobal()
  const { mutate: updateGlobal, isLoading: loadingUpdateGlobal } =
    useUpdateGlobal()

  const handleSave = (data: any) => {
    // ViewOnly users cannot modify values or save
    if (isViewOnly) return

    const startDay = Number(data.startDay)
    const startMonth = Number(data.startMonth)

    // Convert units to metric before API submission
    const siteAreaMetric = isMetric
      ? data.siteArea
      : convertToMetricV2(data.siteArea, 'm2', 'ft2', 2)

    if (id) {
      const payload = {
        siteAreaSize: siteAreaMetric,
        startDay: startDay,
        startMonth: startMonth,
        interfaceId: Number(interfaceId),
        id: id,
      }
      updateGlobal(payload, {
        onSuccess: () => {
          setReloadTurf((prev) => prev + 1)
        },
      })
    } else {
      const payload = {
        siteAreaSize: siteAreaMetric,
        startDay: startDay,
        startMonth: startMonth,
        interfaceId: Number(interfaceId),
      }
      addGlobal(payload, {
        onSuccess: () => {
          setReloadTurf((prev) => prev + 1)
        },
      })
    }
  }

  useEffect(() => {
    if (data && data.list.length > 0) {
      setId(data.list[0].id)
    } else {
      setId(undefined)
    }
  }, [data])

  useEffect(() => {
    if (dataGetId) {
      form.reset({
        ...dataGetId,
        siteArea: convertFromMetricV2(dataGetId.siteAreaSize, 'm2', 'ft2'),
        startMonth: dataGetId.startMonth,
        startDay: dataGetId.startDay,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGetId])

  useEffect(() => {
    const day = Number(form.getValues('startDay'))
    if (day > maxDay) {
      form.setValue('startDay', maxDay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDay])

  const handleDelete = (id: number | string, interfaceId: number | string) => {
    // ViewOnly users cannot delete water source
    if (isViewOnly) return

    openModal()
    setModalView({
      type: 'DELETE_WATER_SOURCE_VIEW',
      id: id.toString(),
      interfaceId: String(interfaceId),

      onSuccess: async () => {
        const newData = await refetch()
        // if (newData.data?.list.length === 0 && currentPage !== 1) {
        //   setCurrentPage(currentPage - 1)
        // }

        // const newCurrentPage =
        //   newData.data?.list.length === 0 && currentPage !== 1
        //     ? currentPage - 1
        //     : currentPage

        setSearchParams({
          ...searchParams,
          // PageIndex: newCurrentPage,
        })
      },
    })
  }
  const handleDesc = (sortBy: SortBy) => {
    return
  }

  const handleEsc = (sortBy: SortBy) => {
    return
  }

  return (
    <Stack className={classes.container}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <Box
          className={classes.boxGlobal}
          sx={{
            border: {
              ...(themeMode === mode_type.dark
                ? { border: '1px solid #2a4552' }
                : { border: '1px solid #d7d7d7' }),
            },
          }}
        >
          <Stack
            className={classes.groupHeader}
            direction="row"
            justifyContent="space-between"
          >
            <Typography
              className={classes.txtGlobal}
              color="primary.contrastText"
            >
              {t('dataConfigLabelGlobalConfigurations')}
            </Typography>
            <LoadingButton
              loading={id ? loadingUpdateGlobal : loadingAddGlobal}
              type="submit"
              variant={isViewOnly ? 'outlined' : 'primary'}
              disabled={isViewOnly}
              sx={{
                opacity: isViewOnly ? 0.6 : 1,
                cursor: isViewOnly ? 'not-allowed' : 'pointer',
              }}
            >
              {t('commonActionsSave')}
            </LoadingButton>
          </Stack>
          <Stack p="15px">
            <InputLabelField
              name="siteArea"
              form={form}
              label={`${t('globalConfigTableSiteAreaSize')} (${convertLabelUnit('m2')})`}
              placeholder="0.00"
              padding="8px 16px"
              disabled={isViewOnly}
              style={{
                height: 50,
                borderRadius: '4px',
                width: { xs: '442px' },
                marginTop: '8px',
                opacity: isViewOnly ? 0.6 : 1,
              }}
            />

            <Divider sx={{ marginTop: '10px', width: '442px' }} />

            <Typography
              sx={{
                color: 'text.main',
                fontSize: '12px',
                fontWeight: 700,
                marginTop: '12px',
                marginBottom: '4px',
              }}
            >
              {t('globalConfigTableFinancialYearStartDate')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ width: '442px' }}>
              <Controller
                name="startMonth"
                control={form.control}
                render={({ field }) => (
                  <FormControl
                    variant="standard"
                    disabled={isViewOnly}
                    sx={{ flex: 1 }}
                  >
                    <Select
                      {...field}
                      value={field.value ?? ''}
                      disableUnderline
                      IconComponent={ArrowDownIcon}
                      sx={{
                        height: 50,
                        borderRadius: '4px',
                        backgroundColor: 'background.overrideDefault',
                        color: 'text.main',
                        fontSize: '12px',
                        opacity: isViewOnly ? 0.6 : 1,
                        '& .MuiSelect-select': {
                          padding: '14px 16px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiSelect-icon': {
                          right: '12px',
                        },
                      }}
                    >
                      {monthOptions.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {t(m.key)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="startDay"
                control={form.control}
                render={({ field }) => (
                  <FormControl
                    variant="standard"
                    disabled={isViewOnly}
                    sx={{ flex: 1 }}
                  >
                    <Select
                      {...field}
                      value={field.value ?? ''}
                      disableUnderline
                      IconComponent={ArrowDownIcon}
                      sx={{
                        height: 50,
                        borderRadius: '4px',
                        backgroundColor: 'background.overrideDefault',
                        color: 'text.main',
                        fontSize: '12px',
                        opacity: isViewOnly ? 0.6 : 1,
                        '& .MuiSelect-select': {
                          padding: '14px 16px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiSelect-icon': {
                          right: '12px',
                        },
                      }}
                    >
                      {dayOptions.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>
          </Stack>
        </Box>
        {!data && (
          <Stack className={classes.circularProgress}>
            <CircularProgress />
          </Stack>
        )}
      </form>
      <TurfQuality reloadKey={reloadTurf} />
      {/* <Box
          className={classes.boxTurfQuality}
          sx={{
            border: {
              ...(themeMode === mode_type.dark
                ? { border: '1px solid #2a4552' }
                : { border: '1px solid #d7d7d7' }),
            },
          }}
        >
          <Stack
            className={classes.groupHeader}
            direction="row"
            justifyContent="space-between"
          >
            <Typography
              className={classes.txtGlobal}
              color="primary.contrastText"
            >
              {t('turfQuality')}
            </Typography>
            <LoadingButton
              loading={id ? loadingUpdateGlobal : loadingAddGlobal}
              type="submit"
              variant={isViewOnly ? 'outlined' : 'primary'}
              disabled={isViewOnly}
              sx={{
                opacity: isViewOnly ? 0.6 : 1,
                cursor: isViewOnly ? 'not-allowed' : 'pointer',
              }}
            >
              {t('commonActionsAdd')}
            </LoadingButton>
          </Stack>
          <MotionDiv
            initial="initial"
            animate="animate"
            variants={flyInVariantsDashboard}
            className={classes.content}
          >
            {!data && (
              <Stack className={classes.circleProgress}>
                <CircularProgress />
              </Stack>
            )}

            {dataTurf && dataTurf?.list.length > 0 && (
              <TurfQualityTable
                rows={dataTurf?.list}
                onDelete={handleDelete}
              />
            )}

            {data && data.totalCount === 0 && (
              <NoData text={t('commonStatusNoData')} />
            )}
          </MotionDiv>
        </Box> */}
    </Stack>
  )
}

export default GlobalConfigurations
function openModal() {
  throw new Error('Function not implemented.')
}

function setModalView(arg0: {
  type: string
  id: string
  interfaceId: string
  onSuccess: () => Promise<void>
}) {
  throw new Error('Function not implemented.')
}
